import { IInvitedReviewer, IReviewer } from '@publishing-engine/domain/models';
import { ISearchQueryItems, ISearchQueryProperty } from '../api/queries';
import { IInviteReviewer } from '../components/Submission/ReviewTab/InviteReviewer/commonTypes';

export const SCORE_TEXT: {
  [key: string]: { scoreTitle: string; scoreText: string };
} = {
  VERY_LOW: {
    scoreTitle: 'Very Low',
    scoreText: 'Does not meet the criteria for this journal.'
  },
  LOW: {
    scoreTitle: 'Low',
    scoreText:
      'Requires major improvement to meet the criteria for this journal.'
  },
  MEDIUM: {
    scoreTitle: 'Medium',
    scoreText: 'Partially meets the criteria for this journal.'
  },
  HIGH: {
    scoreTitle: 'High',
    scoreText:
      'Requires minor improvement to meet the criteria for this journal.'
  },
  VERY_HIGH: {
    scoreTitle: 'Very High',
    scoreText:
      'Meets the criteria for this journal without further improvement.'
  },
  DEFAULT: {
    scoreTitle: '',
    scoreText: ''
  }
};

export const RATING_OPTIONS: Record<string, { label: string; value: number }> =
  {
    poor: { label: 'Poor', value: 1 },
    weak: { label: 'Weak', value: 2 },
    good: { label: 'Good', value: 3 },
    excellent: { label: 'Excellent', value: 4 },
    outstanding: { label: 'Outstanding', value: 5 }
  };

export const SCORE_MAP: Record<string, { text: string; valueNumber: number }> =
  {
    VERY_LOW: { text: '1 (Poor).', valueNumber: 1 },
    LOW: { text: '2 (Weak).', valueNumber: 2 },
    MEDIUM: { text: '3 (Good).', valueNumber: 3 },
    HIGH: { text: '4 (Excellent).', valueNumber: 4 },
    VERY_HIGH: { text: '5 (Outstanding).', valueNumber: 5 },
    DEFAULT: { text: 'Unknown', valueNumber: 0 }
  };

// TODO: COMA-1423 Implement Guidelines links/docs
export const peerReviewGuidelinePolicyLink =
  'https://publishingsupport.iopscience.iop.org/publishing-support/reviewers/assessing-an-article/';

// Submission Peer Review Phases
export const submissionPeerReviewStatuses = [
  'peerReviewInvitation',
  'peerReviewPending',
  'peerReviewFinished',
  'revisionAssessment',
  'revisionPeerReviewInvitation',
  'revisionPeerReviewPending',
  'revisionPeerReviewFinished'
];

export const isReviewRescinded = (currentState: string) => {
  return currentState === 'rescinded';
};

export const getReviewerNameOrEmail = (
  reviewer: Partial<IReviewer> | IInvitedReviewer
) =>
  reviewer.firstName === 'Invited' && reviewer.lastName === 'User'
    ? reviewer.email
    : `${reviewer.firstName} ${reviewer.lastName}`;

export const reviewInProgressSubmissionStatuses = ['firstAssessment'];

export const getEmailFromReviewer = (reviewer: ISearchQueryItems) => {
  return (
    reviewer.properties
      .find(p => p.propertyName === 'ReviewerPrimaryEmailAddress')
      ?.propertyValueString?.[0]?.trim() || ''
  );
};

/**
 * Extracts the appropriate value from a property list based on its datatype.
 *
 * @param properties - Array of ISearchQueryProperty objects
 * @param key - The propertyName to find
 * @returns The first value of the matched datatype field, or "-" if not found
 */
export const getReviewerPropertyValue = (
  properties: ISearchQueryProperty[],
  key: string
): string => {
  const prop = properties?.find(p => p.propertyName === key);
  if (!prop) return '-';
  const fieldMap: Record<
    string,
    { field: keyof ISearchQueryProperty; isArray: boolean }
  > = {
    'xsd:decimal': { field: 'propertyDouble', isArray: true },
    'xsd:integer': { field: 'propertyValueInt', isArray: true },
    'xsd:string': { field: 'propertyValueString', isArray: true },
    'xsd:dateTime': { field: 'propertyDate', isArray: false }
  };

  const mapping = fieldMap[prop.propertyDatatype];
  if (!mapping) return '-';

  const value = prop[mapping.field];
  if (value === undefined || value === null) return '-';

  return mapping.isArray
    ? (value as unknown[])?.length
      ? (value as unknown[])?.join(',')
      : '-'
    : String(value);
};

export const isJournalManagerUser = (item: ISearchQueryItems): boolean =>
  item.properties.some(p => p.propertyName === 'ReviewerUserID') &&
  item.properties.find(p => p.propertyName === 'journalManagerUser')
    ?.propertyBoolean === true;

export const isJournalManagerPartialUser = (item: ISearchQueryItems): boolean =>
  item.properties.some(p => p.propertyName === 'ReviewerUserID');

export const mapSearchReviewerToInviteReviewer = (
  entry: ISearchQueryItems
): IInviteReviewer | null => {
  const email =
    entry.properties
      .find(p => p.propertyName === 'ReviewerPrimaryEmailAddress')
      ?.propertyValueString?.[0]?.trim() || '';

  return {
    email,
    source: 'search',
    meta: {
      id: getReviewerPropertyValue(entry.properties, 'ReviewerUserID'),
      name: getReviewerPropertyValue(entry.properties, 'ReviewerDisplayname'),
      institute: [
        getReviewerPropertyValue(entry.properties, 'ReviewerInstitution'),
        getReviewerPropertyValue(entry.properties, 'ReviewerDepartment'),
        getReviewerPropertyValue(entry.properties, 'ReviewerCountryOrRegion')
      ]
        .filter(value => value && value !== '-')
        .join(', '),
      avgRScore: getReviewerPropertyValue(entry.properties, 'AverageRScore'),
      activeReviews: getReviewerPropertyValue(entry.properties, 'ActiveReviews')
    }
  };
};
